import { parseSiteConfigData } from './siteSeo.js'

export const PIXEL_ENTRY_HOST_SESSION_KEY = 'pixelEntryHost'
export const PIXEL_DISABLED_DOMAINS_CONFIG_KEY = 'pixelDisabledDomains'

function isClientRuntime() {
  return Boolean(process.client && typeof window !== 'undefined')
}

export function normalizePixelDomainHost(value) {
  if (value === undefined || value === null) return ''

  const normalizedValue = `${value}`.trim().toLowerCase()
  if (!normalizedValue) return ''

  try {
    const parsedUrl = new URL(
      /^https?:\/\//i.test(normalizedValue) ? normalizedValue : `https://${normalizedValue}`,
    )
    return parsedUrl.hostname.replace(/\.$/, '')
  }
  catch {
    return normalizedValue
      .replace(/^https?:\/\//i, '')
      .replace(/[/?#].*$/, '')
      .replace(/:\d+$/, '')
      .replace(/\.$/, '')
  }
}

function safeSessionStorageGet(key) {
  if (!isClientRuntime()) return ''
  try {
    return sessionStorage.getItem(key) || ''
  }
  catch {
    return ''
  }
}

function safeSessionStorageSet(key, value) {
  if (!isClientRuntime() || !value) return
  try {
    sessionStorage.setItem(key, value)
  }
  catch {
    // Storage restrictions must not block storefront rendering.
  }
}

function collectManagedDomainHosts(configData) {
  const parsedConfigData = parseSiteConfigData(configData)
  const domainTree = Array.isArray(parsedConfigData?.domainTree) ? parsedConfigData.domainTree : []
  const hosts = new Set()

  domainTree.forEach((node) => {
    const primaryHost = normalizePixelDomainHost(node?.domain)
    if (primaryHost) hosts.add(primaryHost)

    const children = Array.isArray(node?.children) ? node.children : []
    children.forEach((child) => {
      const childHost = normalizePixelDomainHost(child?.domain)
      if (childHost) hosts.add(childHost)
    })
  })

  return hosts
}

function getRedirectCandidate(inputUrl) {
  try {
    const currentUrl = inputUrl instanceof URL
      ? inputUrl
      : new URL(inputUrl || window.location.href, window.location.origin)
    const rawRedirectValue = currentUrl.searchParams.get('redirectSubDomain') || ''
    return normalizePixelDomainHost(rawRedirectValue.split('?')[0])
  }
  catch {
    return ''
  }
}

export function capturePixelEntryHost(inputUrl) {
  if (!isClientRuntime()) return ''

  const storedHost = normalizePixelDomainHost(safeSessionStorageGet(PIXEL_ENTRY_HOST_SESSION_KEY))
  if (storedHost) return storedHost

  const redirectHost = getRedirectCandidate(inputUrl)
  const currentHost = normalizePixelDomainHost(window.location.hostname || window.location.host)
  const entryHost = redirectHost || currentHost
  safeSessionStorageSet(PIXEL_ENTRY_HOST_SESSION_KEY, entryHost)
  return entryHost
}

export function getPixelEntryHost(configData, inputUrl) {
  if (!isClientRuntime()) return ''

  const entryHost = capturePixelEntryHost(inputUrl)
  const currentHost = normalizePixelDomainHost(window.location.hostname || window.location.host)
  if (!entryHost || entryHost === currentHost) return currentHost || entryHost

  // redirectSubDomain is client-controlled. Only accept it when the site config
  // confirms that it belongs to the current shop's managed domain tree.
  return collectManagedDomainHosts(configData).has(entryHost) ? entryHost : currentHost
}

export function getDisabledPixelDomains(configData, platform) {
  const parsedConfigData = parseSiteConfigData(configData)
  const disabledDomainConfig = parsedConfigData?.[PIXEL_DISABLED_DOMAINS_CONFIG_KEY]
  const values = disabledDomainConfig && typeof disabledDomainConfig === 'object'
    ? disabledDomainConfig[platform]
    : []

  return [...new Set((Array.isArray(values) ? values : [])
    .map(normalizePixelDomainHost)
    .filter(Boolean))]
}

export function isPlatformPixelDisabled(configData, platform, inputUrl) {
  const entryHost = getPixelEntryHost(configData, inputUrl)
  if (!entryHost) return false
  return getDisabledPixelDomains(configData, platform).includes(entryHost)
}

export function shouldReportPlatformPixel(configData, platform, inputUrl) {
  return !isPlatformPixelDisabled(configData, platform, inputUrl)
}
